import React, { useState, useEffect, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  Card,
  Typography,
  Tag,
  Space,
  Button,
  Input,
  Select,
  DatePicker,
  Avatar,
  Tooltip,
  message,
  Modal,
  Descriptions,
  Statistic,
  Row,
  Col,
  Dropdown
} from 'antd';
import {
  UserOutlined,
  SearchOutlined,
  EyeOutlined,
  CalendarOutlined,
  GoogleOutlined,
  MailOutlined,
  ClockCircleOutlined,
  LoginOutlined,
  LeftOutlined,
  RightOutlined,
  FilterOutlined
} from '@ant-design/icons';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../services/firebase/config';
import { useTheme } from '../ParticleBackground';
import './UsersManagement.css';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState('');
  const [dateRange, setDateRange] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);

  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailModalVisible, setUserDetailModalVisible] = useState(false);

  const { isDark } = useTheme();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const usersQuery = query(
        collection(db, 'users'),
        orderBy('lastLogin', 'desc')
      );

      const snapshot = await getDocs(usersQuery);
      const usersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        lastLogin: doc.data().lastLogin?.toDate()
      }));

      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
      message.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return date.toLocaleString();
  };

  const getActivityStatus = (lastLogin) => {
    if (!lastLogin) return { status: 'never', text: 'Never logged in', color: 'default' };

    const now = new Date();
    const diffHours = (now - lastLogin) / (1000 * 60 * 60);

    if (diffHours < 1) {
      return { status: 'online', text: 'Online', color: 'green' };
    } else if (diffHours < 24) {
      return { status: 'recent', text: 'Active today', color: 'blue' };
    } else if (diffHours < 168) { // 7 days
      return { status: 'week', text: 'Active this week', color: 'orange' };
    } else {
      return { status: 'inactive', text: 'Inactive', color: 'red' };
    }
  };

  const getUserStats = () => {
    const totalUsers = users.length;
    const activeToday = users.filter(user => {
      if (!user.lastLogin) return false;
      const diffHours = (new Date() - user.lastLogin) / (1000 * 60 * 60);
      return diffHours < 24;
    }).length;

    const newThisWeek = users.filter(user => {
      if (!user.createdAt) return false;
      const diffDays = (new Date() - user.createdAt) / (1000 * 60 * 60 * 24);
      return diffDays < 7;
    }).length;

    const googleUsers = users.filter(user => user.photoURL).length;

    return { totalUsers, activeToday, newThisWeek, googleUsers };
  };

  const showUserDetails = (user) => {
    setSelectedUser(user);
    setUserDetailModalVisible(true);
  };

  // TanStack Table Column Definitions
  const columns = useMemo(() => [
    {
      id: 'user',
      accessorFn: row => `${row.name} ${row.email}`,
      header: ({ column }) => {
        const isFiltered = column.getIsFiltered();

        const items = [
          { label: 'Show All', key: 'all' },
          { type: 'divider' },
          { label: 'Google Users', key: 'google', icon: <GoogleOutlined /> },
          { label: 'Email Users', key: 'email', icon: <MailOutlined /> },
        ];

        return (
          <div className="user-column-header">
            <span>User</span>
            <Dropdown
              menu={{
                items,
                onClick: ({ key }) => {
                  column.setFilterValue(key === 'all' ? undefined : key);
                }
              }}
              trigger={['click']}
            >
              <Button
                type="text"
                size="small"
                icon={<FilterOutlined />}
                style={{
                  marginLeft: '4px',
                  color: isFiltered ? '#1890ff' : '#a0a0a0',
                  background: isFiltered ? '#e6f7ff' : 'transparent'
                }}
              />
            </Dropdown>
          </div>
        );
      },
      cell: ({ row }) => {
        const record = row.original;
        const name = record.name;
        return (
          <Space>
            <Avatar
              src={record.photoURL}
              icon={<UserOutlined />}
              style={{ backgroundColor: isDark ? '#1890ff' : '#87d068' }}
            />
            <div>
              <div style={{ fontWeight: 500 }}>{name || 'No name'}</div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {record.email}
              </Text>
            </div>
          </Space>
        );
      },
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue) return true;
        const hasPhoto = !!row.original.photoURL;
        if (filterValue === 'google') return hasPhoto;
        if (filterValue === 'email') return !hasPhoto;
        return true;
      }
    },
    {
      accessorKey: 'role',
      header: 'Role',
      cell: ({ getValue }) => {
        const role = getValue();
        return (
          <Tag color={role === 'admin' ? 'red' : 'blue'}>
            {role?.toUpperCase() || 'USER'}
          </Tag>
        );
      }
    },
    {
      id: 'authMethod',
      header: 'Auth Method',
      accessorFn: row => row.photoURL ? 'Google' : 'Email',
      cell: ({ row }) => (
        <Space>
          {row.original.photoURL ? (
            <Tag icon={<GoogleOutlined />} color="red">Google</Tag>
          ) : (
            <Tag icon={<MailOutlined />} color="blue">Email</Tag>
          )}
        </Space>
      )
    },
    {
      id: 'activityStatus',
      header: 'Activity Status',
      accessorFn: row => row.lastLogin,
      cell: ({ row }) => {
        const activity = getActivityStatus(row.original.lastLogin);
        return <Tag color={activity.color}>{activity.text}</Tag>;
      }
    },
    {
      accessorKey: 'lastLogin',
      header: 'Last Login',
      cell: ({ getValue }) => {
        const lastLogin = getValue();
        return (
          <div>
            <div>{formatDate(lastLogin)}</div>
            {lastLogin && (
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {Math.floor((new Date() - lastLogin) / (1000 * 60 * 60))}h ago
              </Text>
            )}
          </div>
        );
      }
    },
    {
      accessorKey: 'createdAt',
      header: 'Joined',
      cell: ({ getValue }) => formatDate(getValue())
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => showUserDetails(row.original)}
            />
          </Tooltip>
        </Space>
      )
    }
  ], [isDark]);

  // TanStack Table Instance
  const table = useReactTable({
    data: users,
    columns,
    state: {
      globalFilter,
      columnFilters
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      }
    }
  });

  const stats = getUserStats();

  return (
    <div className="users-management-container">
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>👥 Users Management</Title>
        <Text type="secondary">
          Manage and monitor user activity across the platform
        </Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Users"
              value={stats.totalUsers}
              prefix={<UserOutlined />}
              valueStyle={{ color: isDark ? '#1890ff' : '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Today"
              value={stats.activeToday}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="New This Week"
              value={stats.newThisWeek}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Google Users"
              value={stats.googleUsers}
              prefix={<GoogleOutlined />}
              valueStyle={{ color: '#f56565' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters & Actions */}
      <Card style={{ marginBottom: '24px' }}>
        <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space wrap>
            <Search
              placeholder="Search users..."
              allowClear
              style={{ width: 250 }}
              onChange={(e) => setGlobalFilter(e.target.value)}
              value={globalFilter ?? ''}
            />

            <RangePicker
              placeholder={['Start date', 'End date']}
              onChange={setDateRange}
              style={{ width: 250 }}
              disabled
            />
          </Space>
          <Button
            icon={<SearchOutlined />}
            onClick={fetchUsers}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </Card>

      {/* TanStack Table Rendering */}
      <Card className="tanstack-table-card" bodyStyle={{ padding: 0 }}>
        <div className="tanstack-table-container">
          <table className="tanstack-table">
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={columns.length} style={{ textAlign: 'center', padding: '40px' }}>Loading...</td></tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr><td colSpan={columns.length} style={{ textAlign: 'center', padding: '40px' }}>No users found</td></tr>
              ) : (
                table.getRowModel().rows.map(row => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="pagination-container" style={{ padding: '16px' }}>
          <span className="page-info">
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
          </span>

          <Select
            value={table.getState().pagination.pageSize}
            onChange={e => {
              table.setPageSize(Number(e));
            }}
            className="page-size-select"
            options={[
              { label: '10 / page', value: 10 },
              { label: '20 / page', value: 20 },
              { label: '50 / page', value: 50 },
            ]}
          />

          <div className="pagination-buttons">
            <Button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              icon={<LeftOutlined />}
            />
            <Button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              icon={<RightOutlined />}
            />
          </div>
        </div>
      </Card>

      {/* User Detail Modal */}
      <Modal
        title={
          <Space>
            <Avatar
              src={selectedUser?.photoURL}
              icon={<UserOutlined />}
              size="large"
            />
            <div>
              <div>{selectedUser?.name || 'No name'}</div>
              <Text type="secondary">{selectedUser?.email}</Text>
            </div>
          </Space>
        }
        open={userDetailModalVisible}
        onCancel={() => setUserDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setUserDetailModalVisible(false)}>
            Close
          </Button>
        ]}
        width={600}
      >
        {selectedUser && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="User ID">
              {selectedUser.id}
            </Descriptions.Item>
            <Descriptions.Item label="Role">
              <Tag color={selectedUser.role === 'admin' ? 'red' : 'blue'}>
                {selectedUser.role?.toUpperCase() || 'USER'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Authentication Method">
              {selectedUser.photoURL ? (
                <Tag icon={<GoogleOutlined />} color="red">Google Sign-in</Tag>
              ) : (
                <Tag icon={<MailOutlined />} color="blue">Email & Password</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Activity Status">
              {(() => {
                const activity = getActivityStatus(selectedUser.lastLogin);
                return <Tag color={activity.color}>{activity.text}</Tag>;
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="Last Login">
              <Space>
                <LoginOutlined />
                {formatDate(selectedUser.lastLogin)}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Account Created">
              <Space>
                <CalendarOutlined />
                {formatDate(selectedUser.createdAt)}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Profile Photo">
              {selectedUser.photoURL ? (
                <Avatar
                  src={selectedUser.photoURL}
                  size={64}
                />
              ) : (
                <Text type="secondary">No profile photo</Text>
              )}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default UsersManagement;